import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
  ApiError,
  defaultError,
} from "../../lib/redux";
import { triplePSDatasource } from "../../datasource";
import {
  AcceptResult,
  FleetOptimization,
  FleetOptimizationInput,
  FleetOptimizationStatus,
} from "../../models";
import { ScheduleComparison } from "../../models/ScheduleComparison";

export interface FleetOptimizationState {
  runs: FleetOptimization[];
  currentRun?: FleetOptimization;
  listAPI: APIResult<FleetOptimization[]>;
  createAPI: APIResult<{ id: string; status: FleetOptimizationStatus }>;
  getAPI: APIResult<FleetOptimization>;
  acceptAPI: APIResult<AcceptResult>;
  compareAPI: APIResult<ScheduleComparison>;
  comparisonsAPI: APIResult<ScheduleComparison[]>;
  comparisons: ScheduleComparison[];
  currentComparison?: ScheduleComparison;
}

const initialState: FleetOptimizationState = {
  runs: [],
  listAPI: emptyAPIResult(),
  createAPI: emptyAPIResult(),
  getAPI: emptyAPIResult(),
  acceptAPI: emptyAPIResult(),
  compareAPI: emptyAPIResult(),
  comparisonsAPI: emptyAPIResult(),
  comparisons: [],
};

export interface ListRunsArg {
  date: string;
  limit?: number;
  offset?: number;
}

export const listRuns = createAsyncThunk<
  FleetOptimization[],
  ListRunsArg,
  { rejectValue: ApiError }
>(
  "fleetOptimization/listRuns",
  async ({ date, limit, offset }, { rejectWithValue }) => {
    try {
      return await triplePSDatasource.listFleetOptimizations(date, limit, offset);
    } catch (err: any) {
      const error = err.response?.data?.errors?.[0] || defaultError;
      return rejectWithValue(error);
    }
  }
);

export const createRun = createAsyncThunk<
  { id: string; status: FleetOptimizationStatus },
  FleetOptimizationInput,
  { rejectValue: ApiError }
>("fleetOptimization/createRun", async (input, { rejectWithValue }) => {
  try {
    return await triplePSDatasource.createFleetOptimization(input);
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const pollRun = createAsyncThunk<
  FleetOptimization,
  string,
  { rejectValue: ApiError }
>("fleetOptimization/pollRun", async (id, { rejectWithValue }) => {
  try {
    return await triplePSDatasource.getFleetOptimization(id);
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export interface AcceptRunArg {
  id: string;
  planIndex: number;
  dryRun?: boolean;
}

export const acceptRun = createAsyncThunk<
  AcceptResult,
  AcceptRunArg,
  { rejectValue: ApiError }
>(
  "fleetOptimization/acceptRun",
  async ({ id, planIndex, dryRun }, { rejectWithValue }) => {
    try {
      return await triplePSDatasource.acceptFleetOptimization(id, planIndex, dryRun);
    } catch (err: any) {
      const data = err.response?.data;
      // Validation/infeasible errors use the standard { errors:[...] } envelope;
      // a stale run returns a custom { message, changed:[...] } 409 body.
      const error: ApiError = data?.errors?.[0]
        ? data.errors[0]
        : data?.message
        ? { code: "conflict", title: data.message, detail: "" }
        : defaultError;
      return rejectWithValue(error);
    }
  }
);

export const compareRun = createAsyncThunk<
  ScheduleComparison,
  string,
  { rejectValue: ApiError }
>("fleetOptimization/compareRun", async (id, { rejectWithValue }) => {
  try {
    return await triplePSDatasource.compareFleetOptimization(id);
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const listComparisons = createAsyncThunk<
  ScheduleComparison[],
  string,
  { rejectValue: ApiError }
>("fleetOptimization/listComparisons", async (id, { rejectWithValue }) => {
  try {
    return await triplePSDatasource.listComparisons(id);
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const clearComparisons = createAsyncThunk<
  void,
  string,
  { rejectValue: ApiError }
>("fleetOptimization/clearComparisons", async (id, { rejectWithValue }) => {
  try {
    await triplePSDatasource.clearComparisons(id);
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

const fleetOptimizationSlice = createSlice({
  name: "fleetOptimization",
  initialState,
  reducers: {
    clearAcceptAPI(state) {
      state.acceptAPI = emptyAPIResult();
    },
    selectComparison(state, action: PayloadAction<ScheduleComparison>) {
      state.currentComparison = action.payload;
    },
    clearCompareAPI(state) {
      state.compareAPI = emptyAPIResult();
    },
    // Reset all comparison state — called when the scorecard switches runs so a
    // previous run's comparison never bleeds into another run's view.
    resetComparisons(state) {
      state.comparisons = [];
      state.currentComparison = undefined;
      state.comparisonsAPI = emptyAPIResult();
      state.compareAPI = emptyAPIResult();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listRuns.pending, (state) => {
        state.listAPI = loadingAPIResult();
      })
      .addCase(listRuns.fulfilled, (state, action) => {
        state.listAPI = successAPIResult(action.payload);
        state.runs = action.payload;
      })
      .addCase(listRuns.rejected, (state, action) => {
        state.listAPI = errorAPIResult(action.error);
      })
      .addCase(createRun.pending, (state) => {
        state.createAPI = loadingAPIResult();
      })
      .addCase(createRun.fulfilled, (state, action) => {
        state.createAPI = successAPIResult(action.payload);
      })
      .addCase(createRun.rejected, (state, action) => {
        state.createAPI = errorAPIResult(action.payload!);
      })
      .addCase(pollRun.pending, (state) => {
        state.getAPI = loadingAPIResult();
      })
      .addCase(pollRun.fulfilled, (state, action) => {
        state.getAPI = successAPIResult(action.payload);
        state.currentRun = action.payload;
      })
      .addCase(pollRun.rejected, (state, action) => {
        state.getAPI = errorAPIResult(action.payload!);
      })
      .addCase(acceptRun.pending, (state) => {
        state.acceptAPI = loadingAPIResult();
      })
      .addCase(acceptRun.fulfilled, (state, action) => {
        state.acceptAPI = successAPIResult(action.payload);
      })
      .addCase(acceptRun.rejected, (state, action) => {
        state.acceptAPI = errorAPIResult(action.payload!);
      })
      .addCase(compareRun.pending, (state) => {
        state.compareAPI = loadingAPIResult();
      })
      .addCase(compareRun.fulfilled, (state, action) => {
        state.compareAPI = successAPIResult(action.payload);
        state.currentComparison = action.payload;
        state.comparisons = [action.payload, ...state.comparisons];
      })
      .addCase(compareRun.rejected, (state, action) => {
        state.compareAPI = errorAPIResult(action.payload!);
      })
      .addCase(listComparisons.pending, (state) => {
        state.comparisonsAPI = loadingAPIResult();
      })
      .addCase(listComparisons.fulfilled, (state, action) => {
        state.comparisonsAPI = successAPIResult(action.payload);
        state.comparisons = action.payload;
        if (!state.currentComparison && action.payload.length > 0) {
          state.currentComparison = action.payload[0];
        }
      })
      .addCase(listComparisons.rejected, (state, action) => {
        state.comparisonsAPI = errorAPIResult(action.payload!);
      })
      .addCase(clearComparisons.fulfilled, (state) => {
        state.comparisons = [];
        state.currentComparison = undefined;
        state.comparisonsAPI = emptyAPIResult();
        state.compareAPI = emptyAPIResult();
      });
  },
});

export const { clearAcceptAPI, selectComparison, clearCompareAPI, resetComparisons } = fleetOptimizationSlice.actions;

export default fleetOptimizationSlice.reducer;
